<!--
  WilcomLetteringPrototype.vue -- Wilcom EWA capability-check demo.

  PROTOTYPE, NOT PRODUCTION. Standalone page that renders lettering text
  as a real embroidery TrueView via the Wilcom Embroidery Web API. Type
  text, pick an alphabet + color + height, and get back a stitch-accurate
  preview plus the embroidery metadata (stitch count, colors, size) that
  drives decoration pricing.

  All Wilcom calls go through the server-side proxy
  (api/src/endpoints/wilcom_preview.py) so the Wilcom appId/appKey never
  reach the browser:
    * GET  /api/wilcom/info              -> alphabet list
    * POST /api/wilcom/lettering-preview -> TrueView PNG + design_info

  Gated superadmin.settings; reachable from the SuperAdmin Operations
  sidebar (Prototypes section) and by direct URL
  (/admin/prototypes/wilcom-lettering).

  Findings + live-proof detail:
    docs/integrations/wilcom/2026-07-01-worknote-wilcom-live-proof.md
    docs/integrations/wilcom/capability-verified-2026-07-01.md
-->
<template>
  <div class="wilcom-proto" data-testid="wilcom-lettering-prototype">
    <header class="wilcom-proto__head">
      <h1 class="wilcom-proto__title">Wilcom Lettering -- embroidery preview</h1>
      <p class="wilcom-proto__sub">
        Type text and render it as a real embroidery TrueView through the
        Wilcom EWA. Returns a stitch-accurate preview plus the stitch /
        color / size metadata that feeds decoration pricing. Credentials
        stay server-side.
      </p>
    </header>

    <div class="wilcom-proto__body">
      <!-- LEFT: controls -->
      <section class="wilcom-proto__controls" data-testid="wilcom-proto-controls">
        <div class="wilcom-proto__field">
          <label class="wilcom-proto__label" for="wilcom-text">Text</label>
          <input
            id="wilcom-text"
            v-model="text"
            type="text"
            maxlength="64"
            class="wilcom-proto__input"
            data-testid="wilcom-proto-text"
            placeholder="ThreadFlow"
          />
        </div>

        <div class="wilcom-proto__field">
          <label class="wilcom-proto__label" for="wilcom-alphabet">Alphabet</label>
          <select
            id="wilcom-alphabet"
            v-model="alphabet"
            class="wilcom-proto__input"
            data-testid="wilcom-proto-alphabet"
            :disabled="alphabets.length === 0"
          >
            <option v-for="a in alphabets" :key="a" :value="a">{{ a }}</option>
          </select>
          <p
            v-if="alphabetsError"
            class="wilcom-proto__hint wilcom-proto__hint--error"
            data-testid="wilcom-proto-alphabets-error"
          >
            Could not load alphabets: {{ alphabetsError }}
          </p>
          <p v-else class="wilcom-proto__hint" data-testid="wilcom-proto-alphabets-count">
            {{ alphabets.length }} alphabets available
            <span v-if="apiVersion"> (EWA v{{ apiVersion }})</span>
          </p>
        </div>

        <div class="wilcom-proto__field-row">
          <div class="wilcom-proto__field">
            <label class="wilcom-proto__label" for="wilcom-color">Thread color</label>
            <input
              id="wilcom-color"
              v-model="color"
              type="color"
              class="wilcom-proto__color"
              data-testid="wilcom-proto-color"
            />
          </div>
          <div class="wilcom-proto__field">
            <label class="wilcom-proto__label" for="wilcom-height">Height (mm)</label>
            <input
              id="wilcom-height"
              v-model.number="heightMm"
              type="number"
              min="1"
              max="500"
              class="wilcom-proto__input"
              data-testid="wilcom-proto-height"
            />
          </div>
        </div>

        <button
          class="wilcom-proto__btn"
          :disabled="!canRender || renderStatus === 'rendering'"
          data-testid="wilcom-proto-render-btn"
          @click="onRender"
        >
          {{ renderStatus === 'rendering' ? 'Rendering...' : 'Render preview' }}
        </button>

        <p
          v-if="renderError"
          class="wilcom-proto__error"
          data-testid="wilcom-proto-render-error"
        >
          {{ renderError }}
        </p>
      </section>

      <!-- RIGHT: result -->
      <section class="wilcom-proto__result" data-testid="wilcom-proto-result">
        <div class="wilcom-proto__stage">
          <img
            v-if="imageUrl"
            :src="imageUrl"
            alt="Embroidery TrueView preview"
            class="wilcom-proto__image"
            data-testid="wilcom-proto-image"
          />
          <p v-else class="wilcom-proto__placeholder">
            The rendered embroidery preview appears here.
          </p>
        </div>

        <dl
          v-if="designInfo"
          class="wilcom-proto__meta"
          data-testid="wilcom-proto-design-info"
        >
          <div class="wilcom-proto__meta-row">
            <dt>Stitches</dt>
            <dd data-testid="wilcom-proto-stitches">{{ designInfo.num_stitches }}</dd>
          </div>
          <div class="wilcom-proto__meta-row">
            <dt>Colors</dt>
            <dd>{{ designInfo.num_colours }}</dd>
          </div>
          <div class="wilcom-proto__meta-row">
            <dt>Trims</dt>
            <dd>{{ designInfo.num_trims }}</dd>
          </div>
          <div class="wilcom-proto__meta-row">
            <dt>Size (mm)</dt>
            <dd>{{ formatSize(designInfo) }}</dd>
          </div>
          <div class="wilcom-proto__meta-row">
            <dt>Machine</dt>
            <dd>{{ designInfo.machine_name }}</dd>
          </div>
        </dl>
      </section>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

// Wilcom's thread color is packed R | G<<8 | B<<16 (0xBBGGRR), NOT the
// conventional 0xRRGGBB. The backend does the actual packing from the hex
// we send; this helper is only for a client-side sanity display if needed.
// Extracted as a pure function so it is unit-testable in isolation.
export function hexToWilcomColor(hex) {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex || '')
  if (!m) return null
  const d = m[1]
  const r = parseInt(d.slice(0, 2), 16)
  const g = parseInt(d.slice(2, 4), 16)
  const b = parseInt(d.slice(4, 6), 16)
  return r | (g << 8) | (b << 16)
}

export default {
  name: 'WilcomLetteringPrototype',
  data() {
    return {
      text: 'ThreadFlow',
      alphabet: 'Block1',
      color: '#0033aa',
      heightMm: 15,
      alphabets: [],
      apiVersion: null,
      alphabetsError: null,
      renderStatus: 'idle',
      renderError: null,
      imageUrl: null,
      designInfo: null,
    }
  },
  computed: {
    canRender() {
      return (
        typeof this.text === 'string' &&
        this.text.trim().length > 0 &&
        !!this.alphabet
      )
    },
  },
  async mounted() {
    await this.loadAlphabets()
  },
  methods: {
    async loadAlphabets() {
      this.alphabetsError = null
      try {
        const res = await api.get('/wilcom/info')
        this.alphabets = res.data.alphabets || []
        this.apiVersion = res.data.api_version || null
        // Keep the default alphabet if present, else fall back to the first.
        if (this.alphabets.length && !this.alphabets.includes(this.alphabet)) {
          this.alphabet = this.alphabets[0]
        }
      } catch (e) {
        this.alphabetsError = this.errText(e)
        this.alphabets = []
      }
    },
    async onRender() {
      this.renderStatus = 'rendering'
      this.renderError = null
      try {
        const res = await api.post('/wilcom/lettering-preview', {
          text: this.text,
          alphabet: this.alphabet,
          color: this.color,
          height_mm: this.heightMm,
        })
        this.imageUrl = res.data.image_url
        this.designInfo = res.data.design_info
        this.renderStatus = 'done'
      } catch (e) {
        this.renderError = this.errText(e)
        this.renderStatus = 'error'
      }
    },
    errText(e) {
      return (
        e?.response?.data?.error?.message ||
        e?.message ||
        'Request failed'
      )
    },
    formatSize(info) {
      if (!info) return ''
      const w = info.width_mm != null ? info.width_mm : '?'
      const h = info.height_mm != null ? info.height_mm : '?'
      return `${w} x ${h}`
    },
  },
}
</script>

<style scoped>
.wilcom-proto {
  max-width: 980px;
}
.wilcom-proto__title {
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 6px;
  color: #1a202c;
}
.wilcom-proto__sub {
  font-size: 14px;
  color: #4a5568;
  margin: 0 0 24px;
  max-width: 60ch;
}
.wilcom-proto__body {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}
.wilcom-proto__controls {
  flex: 0 0 320px;
}
.wilcom-proto__result {
  flex: 1;
  min-width: 0;
}
.wilcom-proto__field {
  margin-bottom: 16px;
}
.wilcom-proto__field-row {
  display: flex;
  gap: 16px;
}
.wilcom-proto__field-row .wilcom-proto__field {
  flex: 1;
}
.wilcom-proto__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #4a5568;
  margin-bottom: 4px;
}
.wilcom-proto__input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}
.wilcom-proto__color {
  width: 100%;
  height: 38px;
  padding: 2px;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  box-sizing: border-box;
}
.wilcom-proto__hint {
  font-size: 12px;
  color: var(--tf-fg-4);
  margin: 6px 0 0;
}
.wilcom-proto__hint--error {
  color: #c53030;
}
.wilcom-proto__btn {
  width: 100%;
  padding: 10px 16px;
  background: #2b6cb0;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
}
.wilcom-proto__btn:disabled {
  background: #a0aec0;
  cursor: not-allowed;
}
.wilcom-proto__error {
  color: #c53030;
  font-size: 13px;
  margin-top: 10px;
}
.wilcom-proto__stage {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7fafc;
  border: 1px dashed #cbd5e0;
  border-radius: 8px;
  padding: 24px;
}
.wilcom-proto__image {
  max-width: 100%;
  height: auto;
}
.wilcom-proto__placeholder {
  color: var(--tf-fg-4);
  font-size: 14px;
}
.wilcom-proto__meta {
  margin: 20px 0 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}
.wilcom-proto__meta-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 14px;
  font-size: 13px;
  border-bottom: 1px solid #edf2f7;
}
.wilcom-proto__meta-row:last-child {
  border-bottom: none;
}
.wilcom-proto__meta-row dt {
  color: var(--tf-fg-3);
  margin: 0;
}
.wilcom-proto__meta-row dd {
  color: #1a202c;
  font-weight: 600;
  margin: 0;
}
</style>
